'use client'

import React from 'react'

import { type LucideIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Title Classes Utility
// ============================================================================

/**
 * Get responsive title CSS classes based on title length
 * Longer titles get smaller font sizes to prevent overflow.
 */
function getTitleClasses(length: number): string {
  if (length > 200) {
    return 'text-lg md:text-xl font-bold leading-snug tracking-tight'
  }
  if (length > 120) {
    return 'text-xl md:text-2xl font-bold leading-snug tracking-tight'
  }
  return 'text-2xl md:text-3xl font-bold leading-tight tracking-tight'
}

// ============================================================================
// Root Component
// ============================================================================

interface PageTitleV2Props {
  children: React.ReactNode
  className?: string
}

/**
 * PageTitleV2 - Compositional page header for opportunity detail pages
 *
 * Features:
 * - Compound component pattern with Badges, Content, Actions slots
 * - Dynamic title sizing based on content length
 * - Responsive layout (stacks on mobile, side-by-side on desktop)
 * - Pure components with no side effects
 *
 * @example
 * ```tsx
 * <PageTitleV2>
 *   <PageTitleV2.Badges>
 *     <BadgeV2 variant="info">Contract</BadgeV2>
 *   </PageTitleV2.Badges>
 *
 *   <PageTitleV2.Row>
 *     <PageTitleV2.Content>
 *       <PageTitleV2.Title>Federal Contract Opportunity</PageTitleV2.Title>
 *       <PageTitleV2.Subtitle icon={Building2}>Department of Defense</PageTitleV2.Subtitle>
 *       <PageTitleV2.Meta>SOL-2024-001</PageTitleV2.Meta>
 *     </PageTitleV2.Content>
 *
 *     <PageTitleV2.Actions>
 *       <Button>Share</Button>
 *       <Button>Pursue</Button>
 *     </PageTitleV2.Actions>
 *   </PageTitleV2.Row>
 * </PageTitleV2>
 * ```
 */
function PageTitleV2Root({ children, className }: PageTitleV2Props) {
  return (
    <div className={twMerge('mb-6', className)}>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

PageTitleV2Root.displayName = 'PageTitleV2'

// ============================================================================
// Badges Slot
// ============================================================================

interface BadgesProps {
  children: React.ReactNode
  className?: string
}

/**
 * Container for badges/pills at the top of the header
 */
function PageTitleBadges({ children, className }: BadgesProps) {
  return (
    <div className={twMerge('flex flex-wrap items-center gap-2', className)}>
      {children}
    </div>
  )
}

PageTitleBadges.displayName = 'PageTitleV2.Badges'

// ============================================================================
// Content Slot (Title + Subtitle + Meta)
// ============================================================================

interface ContentProps {
  children: React.ReactNode
  className?: string
}

/**
 * Container for the title block (title, subtitle, meta)
 * Wraps in a flex layout with Actions for responsive behavior
 */
function PageTitleContent({ children, className }: ContentProps) {
  return (
    <div className={twMerge('w-full space-y-1.5 lg:max-w-[75%]', className)}>
      {children}
    </div>
  )
}

PageTitleContent.displayName = 'PageTitleV2.Content'

// ============================================================================
// Title Sub-Component
// ============================================================================

interface TitleProps {
  children: string
  className?: string
}

/**
 * Main page title with dynamic sizing based on content length
 */
function PageTitleTitle({ children, className }: TitleProps) {
  const titleClasses = getTitleClasses(children.length)

  return (
    <h1
      className={twMerge(
        titleClasses,
        'text-primary-900 dark:text-primary-50 line-clamp-3',
        className,
      )}
    >
      {children}
    </h1>
  )
}

PageTitleTitle.displayName = 'PageTitleV2.Title'

// ============================================================================
// Subtitle Sub-Component
// ============================================================================

interface SubtitleProps {
  children: React.ReactNode
  /** Optional leading icon */
  icon?: LucideIcon
  className?: string
}

/**
 * Subtitle line below the title
 *
 * Can be used with an icon for standard layouts:
 * ```tsx
 * <PageTitleV2.Subtitle icon={Building2}>Department of Defense</PageTitleV2.Subtitle>
 * ```
 *
 * Or with custom content for special layouts (e.g., SBIR):
 * ```tsx
 * <PageTitleV2.Subtitle>
 *   <span className="...">Solicitation ID</span>
 *   <code className="...">{solicitationNumber}</code>
 * </PageTitleV2.Subtitle>
 * ```
 */
function PageTitleSubtitle({ children, icon: Icon, className }: SubtitleProps) {
  return (
    <div
      className={twMerge(
        'flex items-center gap-2 text-sm text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {Icon && <Icon className="h-4 w-4 text-primary-400" />}
      {typeof children === 'string' ? (
        <span className="text-primary-600 dark:text-primary-400">
          {children}
        </span>
      ) : (
        children
      )}
    </div>
  )
}

PageTitleSubtitle.displayName = 'PageTitleV2.Subtitle'

// ============================================================================
// Meta Sub-Component
// ============================================================================

interface MetaProps {
  children: React.ReactNode
  className?: string
}

/**
 * Optional tertiary text below subtitle (e.g., solicitation number)
 */
function PageTitleMeta({ children, className }: MetaProps) {
  if (!children) return null

  return (
    <span
      className={twMerge(
        'inline-block text-sm text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {children}
    </span>
  )
}

PageTitleMeta.displayName = 'PageTitleV2.Meta'

// ============================================================================
// Actions Slot
// ============================================================================

interface ActionsProps {
  children: React.ReactNode
  className?: string
}

/**
 * Container for action buttons (Share, Pursue, etc.)
 *
 * Layout: Independent row below title content to prevent button width changes
 * from reflowing the title block.
 */
function PageTitleActions({ children, className }: ActionsProps) {
  return (
    <div
      className={twMerge(
        'flex w-full flex-wrap items-center gap-2 lg:justify-end',
        className,
      )}
    >
      {children}
    </div>
  )
}

PageTitleActions.displayName = 'PageTitleV2.Actions'

// ============================================================================
// Row Layout Component (Content + Actions)
// ============================================================================

interface RowProps {
  children: React.ReactNode
  className?: string
}

/**
 * Flex row container for Content + Actions
 * Uses a vertical layout so title/content and actions are decoupled.
 */
function PageTitleRow({ children, className }: RowProps) {
  return (
    <div className={twMerge('flex flex-col gap-3', className)}>{children}</div>
  )
}

PageTitleRow.displayName = 'PageTitleV2.Row'

// ============================================================================
// Skeleton Sub-Components
// ============================================================================

interface TitleSkeletonProps {
  /** Width of the skeleton (tailwind class like 'w-96' or 'w-full') */
  width?: string
  className?: string
}

/**
 * Skeleton for the title - matches h1 dimensions
 * Height matches text-2xl/3xl line height (~32-36px)
 */
function PageTitleTitleSkeleton({
  width = 'w-96',
  className,
}: TitleSkeletonProps) {
  return (
    <div
      className={twMerge(
        'h-8 animate-pulse rounded bg-primary-200 dark:bg-primary-700',
        width,
        className,
      )}
    />
  )
}

PageTitleTitleSkeleton.displayName = 'PageTitleV2.TitleSkeleton'

interface SubtitleSkeletonProps {
  /** Width of the text portion */
  width?: string
  /** Whether to show the icon placeholder */
  showIcon?: boolean
  className?: string
}

/**
 * Skeleton for subtitle - matches icon + text-sm dimensions
 */
function PageTitleSubtitleSkeleton({
  width = 'w-48',
  showIcon = true,
  className,
}: SubtitleSkeletonProps) {
  return (
    <div className={twMerge('flex items-center gap-2', className)}>
      {showIcon && (
        <div className="h-4 w-4 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
      )}
      <div
        className={twMerge(
          'h-4 animate-pulse rounded bg-primary-200 dark:bg-primary-700',
          width,
        )}
      />
    </div>
  )
}

PageTitleSubtitleSkeleton.displayName = 'PageTitleV2.SubtitleSkeleton'

interface MetaSkeletonProps {
  /** Width of the skeleton */
  width?: string
  className?: string
}

/**
 * Skeleton for meta text - matches text-xs dimensions
 */
function PageTitleMetaSkeleton({
  width = 'w-32',
  className,
}: MetaSkeletonProps) {
  return (
    <div
      className={twMerge(
        'h-3.5 animate-pulse rounded bg-primary-200 dark:bg-primary-700',
        width,
        className,
      )}
    />
  )
}

PageTitleMetaSkeleton.displayName = 'PageTitleV2.MetaSkeleton'

// ============================================================================
// Compound Component Assembly
// ============================================================================

const PageTitleV2 = PageTitleV2Root as typeof PageTitleV2Root & {
  Badges: typeof PageTitleBadges
  Content: typeof PageTitleContent
  Title: typeof PageTitleTitle
  Subtitle: typeof PageTitleSubtitle
  Meta: typeof PageTitleMeta
  Actions: typeof PageTitleActions
  Row: typeof PageTitleRow
  TitleSkeleton: typeof PageTitleTitleSkeleton
  SubtitleSkeleton: typeof PageTitleSubtitleSkeleton
  MetaSkeleton: typeof PageTitleMetaSkeleton
}

PageTitleV2.Badges = PageTitleBadges
PageTitleV2.Content = PageTitleContent
PageTitleV2.Title = PageTitleTitle
PageTitleV2.Subtitle = PageTitleSubtitle
PageTitleV2.Meta = PageTitleMeta
PageTitleV2.Actions = PageTitleActions
PageTitleV2.Row = PageTitleRow
PageTitleV2.TitleSkeleton = PageTitleTitleSkeleton
PageTitleV2.SubtitleSkeleton = PageTitleSubtitleSkeleton
PageTitleV2.MetaSkeleton = PageTitleMetaSkeleton

export { PageTitleV2 }
export type { PageTitleV2Props }
